// ===================================================================
// OpenGov EMC -- Workflow Instance Detail
// Visual timeline of workflow progress with inline actions
// URL: /workflows/instance/:instanceId
// ===================================================================

import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card, Text, Badge, Group, Stack, Button, Modal, Textarea, TextInput, Select,
  Paper, Title, ThemeIcon, Progress, Divider, Loader, Center,
} from '../../components/gab/GabComponents';
import { notifications } from '../../lib/notifications';
import {
  IconCheck, IconX, IconArrowLeft,
  IconCircle, IconPencil, IconAlertTriangle,
  IconWriting, IconListCheck, IconBell, IconPlayerPlay,
  IconUserEdit, IconPlayerPause, IconPlayerPlayFilled,
  IconInfoCircle, IconFileDescription,
} from '@tabler/icons-react';
import { useWorkflowStore } from '../../store/workflowStore';
import { useAuthStore } from '../../store/authStore';
import { useRoleStore } from '../../store/roleStore';
import { useModulePermission } from '../../hooks/useModulePermission';
import type { WorkflowStep, WorkflowStepDef, DocumentCheckItem } from '../../types/workflow';
import {
  STEP_TYPE_LABELS, STEP_TYPE_COLORS,
  INSTANCE_STATUS_COLORS, PRIORITY_COLORS,
} from '../../types/workflow';

// -- Helpers ----------------------------------------------------------

function fmt(iso: string) {
  if (!iso) return '--';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
}

function stepTypeIcon(type: string, size = 14) {
  switch (type) {
    case 'approval': return <IconListCheck size={size} />;
    case 'review': return <IconPencil size={size} />;
    case 'task': return <IconPlayerPlay size={size} />;
    case 'signature': return <IconWriting size={size} />;
    case 'notification': return <IconBell size={size} />;
    default: return <IconCircle size={size} />;
  }
}

// -- Step circle colors -----------------------------------------------

function stepCircleColor(step: WorkflowStep, isCurrentStep: boolean): string {
  if (step.status === 'completed') {
    if (step.outcome === 'rejected') return '#DC2626';
    return '#059669';
  }
  if (step.status === 'rejected') return '#DC2626';
  if (step.status === 'in_progress' || isCurrentStep) return '#0284C7';
  return '#CBD5E1';
}

// -- Action modal types -----------------------------------------------

type ActionType = 'approve' | 'reject' | 'review' | 'task' | 'signature' | null;

// =====================================================================
// Main Component
// =====================================================================

export default function WorkflowInstanceDetail() {
  const { instanceId } = useParams<{ instanceId: string }>();
  const navigate = useNavigate();

  const { templates, instances, steps, loading, loadAll, completeStep, rejectStep, updateInstance, updateStep } = useWorkflowStore();
  const { user } = useAuthStore();
  const { myRole } = useRoleStore();
  const { canEdit } = useModulePermission('workflows');

  // Action modal
  const [actionType, setActionType] = useState<ActionType>(null);
  const [actionStep, setActionStep] = useState<WorkflowStep | null>(null);
  const [comments, setComments] = useState('');
  const [signatureText, setSignatureText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Admin modals
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignEmail, setReassignEmail] = useState('');
  const [userOptions, setUserOptions] = useState<{ value: string; label: string }[]>([]);

  useEffect(() => {
    loadAll();
    import('../../lib/api-switch').then(({ fetchIgnatiusUsers }) => {
      fetchIgnatiusUsers().then((users: any[]) => {
        setUserOptions(users.map(u => ({ value: u.email, label: `${u.fullName} (${u.email})` })));
      }).catch(() => {});
    });
  }, [loadAll]);

  // -- Derived data ---------------------------------------------------

  const instance = instances.find(i => String(i.Rid) === String(instanceId));
  const template = instance ? templates.find(t => String(t.Rid) === String(instance.templateId)) : null;

  const instanceSteps = useMemo(
    () => steps.filter(st => st.instanceId === String(instanceId)).sort((a, b) => Number(a.stepIndex) - Number(b.stepIndex)),
    [steps, instanceId],
  );

  const currentStepIndex = Number(instance?.currentStepIndex ?? -1);
  const totalSteps = Number(instance?.totalSteps ?? 0);
  const completedCount = instanceSteps.filter(s => s.status === 'completed' || s.status === 'rejected').length;
  const progressPct = totalSteps > 0 ? Math.round((completedCount / totalSteps) * 100) : 0;

  const userEmail = user?.email ?? '';
  const isAdmin = canEdit || user?.isAdmin || user?.topLevelRole === 'admin' || user?.topLevelRole === 'super_admin';

  let stepDefs: WorkflowStepDef[] = [];
  try { if (template?.stepsJson) stepDefs = JSON.parse(template.stepsJson); } catch { /* */ }

  // -- Can the current user act on a step? ----------------------------

  function canActOnStep(step: WorkflowStep): boolean {
    if (instance?.status !== 'active') return false;
    if (step.status !== 'in_progress') return false;
    const assigneeLower = (step.assignee || '').toLowerCase();
    if (assigneeLower === userEmail.toLowerCase()) return true;
    if (step.assigneeType === 'role') {
      if (myRole?.roleSlug && assigneeLower === myRole.roleSlug.toLowerCase()) return true;
      if (myRole?.roleName && assigneeLower === myRole.roleName.toLowerCase()) return true;
      return true; // role-based open to claim
    }
    if (step.assigneeType === 'supervisor') return true;
    return false;
  }

  // -- Loading / not found --------------------------------------------

  if (loading && !instance) {
    return <Center py={80}><Loader size="md" /></Center>;
  }

  if (!instance) {
    return (
      <Center py={80}>
        <Stack align="center" gap="sm">
          <ThemeIcon size={60} radius="xl" variant="light" color="gray"><IconAlertTriangle size={30} /></ThemeIcon>
          <Title order={4}>Workflow Not Found</Title>
          <Text size="sm" c="dimmed">This workflow instance does not exist or has been removed.</Text>
          <Button leftSection={<IconArrowLeft size={14} />} variant="light" mt="xs" onClick={() => navigate('/workflows')}>Back to Workflows</Button>
        </Stack>
      </Center>
    );
  }

  // -- Action handlers ------------------------------------------------

  const openAction = (type: ActionType, step: WorkflowStep) => {
    setActionType(type);
    setActionStep(step);
    setComments('');
    setSignatureText('');
  };

  const closeAction = () => {
    if (!submitting) {
      setActionType(null);
      setActionStep(null);
    }
  };

  const handleSubmitAction = async () => {
    if (!actionStep || !user) return;
    setSubmitting(true);
    try {
      const by = user.email;
      if (actionType === 'reject') {
        await rejectStep(actionStep.Rid, by, comments || undefined);
        notifications.show({ message: 'Step rejected', color: 'red' });
      } else {
        const outcome =
          actionType === 'approve' ? 'approved' :
          actionType === 'signature' ? 'signed' : 'completed';
        await completeStep(actionStep.Rid, outcome, by, [comments, actionType === 'signature' ? signatureText : ''].filter(Boolean).join('\n') || undefined);
        notifications.show({ message: actionType === 'approve' ? 'Step approved' : actionType === 'signature' ? 'Signature submitted' : 'Step completed', color: 'green' });
      }
      closeAction();
    } catch (err) {
      notifications.show({ message: `Action failed: ${err}`, color: 'red' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelWorkflow = async () => {
    setSubmitting(true);
    try {
      await updateInstance(instance.Rid, { status: 'cancelled', completedAt: new Date().toISOString() });
      notifications.show({ message: 'Workflow cancelled', color: 'gray' });
      setCancelConfirm(false);
    } catch (err) {
      notifications.show({ message: `Failed: ${err}`, color: 'red' });
    } finally {
      setSubmitting(false);
    }
  };

  const handlePauseResume = async () => {
    setSubmitting(true);
    const newStatus = instance.status === 'paused' ? 'active' : 'paused';
    try {
      await updateInstance(instance.Rid, { status: newStatus });
      notifications.show({ message: newStatus === 'paused' ? 'Workflow paused' : 'Workflow resumed', color: newStatus === 'paused' ? 'yellow' : 'green' });
    } catch (err) {
      notifications.show({ message: `Failed: ${err}`, color: 'red' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleReassign = async () => {
    if (!reassignEmail.trim()) return;
    const currentStep = instanceSteps.find(st => st.status === 'in_progress');
    if (!currentStep) return;
    setSubmitting(true);
    try {
      await updateStep(currentStep.Rid, { assignee: reassignEmail.trim(), assigneeType: 'user', assigneeName: '' });
      notifications.show({ message: `Step reassigned to ${reassignEmail.trim()}`, color: 'blue' });
      setReassignOpen(false);
      setReassignEmail('');
    } catch (err) {
      notifications.show({ message: `Failed: ${err}`, color: 'red' });
    } finally {
      setSubmitting(false);
    }
  };

  const statusColor = INSTANCE_STATUS_COLORS[instance.status as keyof typeof INSTANCE_STATUS_COLORS] ?? '#0284C7';
  const priorityColor = PRIORITY_COLORS[instance.priority as keyof typeof PRIORITY_COLORS] ?? '#0284C7';

  // ===================================================================
  // Render
  // ===================================================================

  return (
    <div>
      {/* Back button */}
      <Button leftSection={<IconArrowLeft size={14} />} variant="subtle" size="xs" mb="md" onClick={() => navigate(-1)}>
        Back
      </Button>

      {/* Header card */}
      <Card withBorder radius="sm" mb="md" p="lg">
        <Group justify="space-between" align="flex-start" wrap="wrap" gap="md">
          <Stack gap={4}>
            <Title order={3}>{instance.templateName || template?.name || 'Workflow'}</Title>
            {instance.sourceRecordLabel && (
              <Text size="sm" c="dimmed">{instance.sourceRecordLabel}</Text>
            )}
            <Group gap="xs" mt={4}>
              <Badge size="sm" style={{ background: statusColor, color: '#fff' }}>
                {instance.status.toUpperCase()}
              </Badge>
              <Badge size="sm" variant="outline" style={{ borderColor: priorityColor, color: priorityColor }}>
                {instance.priority}
              </Badge>
              {instance.sourceModule && instance.sourceModule !== '*' && (
                <Badge size="sm" variant="light" color="gray">{instance.sourceModule}</Badge>
              )}
            </Group>
          </Stack>

          <Stack gap={4} align="flex-end">
            <Text size="xs" c="dimmed">Started by {instance.startedBy || '--'}</Text>
            <Text size="xs" c="dimmed">{fmt(instance.startedAt)}</Text>
            {instance.completedAt && <Text size="xs" c="dimmed">Completed: {fmt(instance.completedAt)}</Text>}

            {/* Admin actions */}
            {isAdmin && instance.status === 'active' && (
              <Group gap="xs" mt={4}>
                <Button size="xs" variant="light" color="blue" leftSection={<IconUserEdit size={12} />} onClick={() => setReassignOpen(true)}>
                  Reassign
                </Button>
                <Button size="xs" variant="light" color="yellow" leftSection={<IconPlayerPause size={12} />} onClick={handlePauseResume} loading={submitting}>
                  Pause
                </Button>
                <Button size="xs" variant="light" color="red" leftSection={<IconX size={12} />} onClick={() => setCancelConfirm(true)}>
                  Cancel
                </Button>
              </Group>
            )}
            {isAdmin && instance.status === 'paused' && (
              <Group gap="xs" mt={4}>
                <Button size="xs" variant="light" color="green" leftSection={<IconPlayerPlayFilled size={12} />} onClick={handlePauseResume} loading={submitting}>
                  Resume
                </Button>
                <Button size="xs" variant="light" color="red" leftSection={<IconX size={12} />} onClick={() => setCancelConfirm(true)}>
                  Cancel
                </Button>
              </Group>
            )}
          </Stack>
        </Group>

        <Divider my="md" />

        {/* Progress */}
        <Stack gap={6}>
          <Group justify="space-between">
            <Text size="xs" fw={600}>Progress</Text>
            <Text size="xs" c="dimmed">
              {completedCount} of {totalSteps} steps complete
            </Text>
          </Group>
          <Progress
            value={progressPct}
            color={instance.status === 'completed' ? 'green' : instance.status === 'cancelled' ? 'gray' : 'blue'}
            size="md" radius="xl"
          />
        </Stack>
      </Card>

      {/* Visual Timeline */}
      <Card withBorder radius="sm" p="lg">
        <Title order={5} mb="md">Steps</Title>

        {instanceSteps.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="lg">No steps found for this workflow.</Text>
        ) : (
          <div style={{ position: 'relative' }}>
            {instanceSteps.map((step, idx) => {
              const isActive = step.status === 'in_progress';
              const isDone = step.status === 'completed' || step.status === 'rejected';
              const isCurrent = idx === currentStepIndex;
              const circleColor = stepCircleColor(step, isCurrent);
              const typeColor = STEP_TYPE_COLORS[step.stepType as keyof typeof STEP_TYPE_COLORS] ?? '#64748B';
              const typeLabel = STEP_TYPE_LABELS[step.stepType as keyof typeof STEP_TYPE_LABELS] ?? step.stepType;
              const def = stepDefs[idx];
              const userCanAct = canActOnStep(step);

              return (
                <div key={step.Rid} style={{ display: 'flex', gap: 16, marginBottom: idx < instanceSteps.length - 1 ? 0 : 0 }}>
                  {/* Vertical line + circle */}
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 32, flexShrink: 0 }}>
                    <div style={{
                      width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      backgroundColor: circleColor, color: '#fff',
                      boxShadow: isActive ? `0 0 0 4px ${circleColor}33` : undefined,
                      transition: 'all 0.2s',
                    }}>
                      <Text size="xs" fw={700} c="white">{idx + 1}</Text>
                    </div>
                    {idx < instanceSteps.length - 1 && (
                      <div style={{
                        width: 2, flex: 1, minHeight: 24,
                        backgroundColor: isDone ? '#059669' : '#E2E8F0',
                      }} />
                    )}
                  </div>

                  {/* Step content */}
                  <Paper
                    p="sm" radius="sm" withBorder mb="sm"
                    style={{
                      flex: 1,
                      borderColor: isActive ? `${circleColor}66` : undefined,
                      backgroundColor: isActive ? '#F0F9FF' : undefined,
                    }}
                  >
                    {/* Step header */}
                    <Group justify="space-between" wrap="wrap" gap="xs" mb={4}>
                      <Group gap="xs" wrap="wrap">
                        <Text size="sm" fw={600}>{step.stepName}</Text>
                        <Badge size="xs" leftSection={stepTypeIcon(step.stepType, 10)} style={{ background: typeColor, color: '#fff' }}>
                          {typeLabel}
                        </Badge>
                        {isActive && <Badge size="xs" color="blue" variant="dot">Current</Badge>}
                        {step.status === 'rejected' && <Badge size="xs" color="red">Rejected</Badge>}
                      </Group>
                      {step.dueDate && (
                        <Text size="xs" c={new Date(step.dueDate) < new Date() ? 'red' : 'dimmed'} ff="monospace">
                          Due: {fmt(step.dueDate)}
                        </Text>
                      )}
                    </Group>

                    {/* Assignee */}
                    <Text size="xs" c="dimmed">
                      Assigned to: {step.assigneeName || step.assignee || '--'}
                      {step.assigneeType === 'role' ? ' (role)' : ''}
                    </Text>

                    {/* Completed step details */}
                    {isDone && (
                      <Stack gap={2} mt={4}>
                        {step.outcome && (
                          <Group gap="xs">
                            <Text size="xs" c="dimmed">Outcome:</Text>
                            <Badge size="xs" color={
                              step.outcome === 'approved' || step.outcome === 'completed' || step.outcome === 'signed' ? 'green'
                              : step.outcome === 'condition_true' ? 'teal'
                              : step.outcome === 'condition_false' ? 'orange'
                              : 'red'
                            }>
                              {step.outcome === 'condition_true' ? 'Condition Met → Routed'
                              : step.outcome === 'condition_false' ? 'Condition Not Met → Routed'
                              : step.outcome}
                            </Badge>
                          </Group>
                        )}
                        {step.completedBy && (
                          <Text size="xs" c="dimmed">
                            {step.stepType === 'signature' ? 'Signed' : 'Completed'} by: {step.completedBy}
                          </Text>
                        )}
                        {step.completedAt && <Text size="xs" c="dimmed">{fmt(step.completedAt)}</Text>}
                        {step.comments && (
                          <Paper p="xs" radius="sm" withBorder mt={2} style={{ backgroundColor: 'var(--mantine-color-gray-0)' }}>
                            <Text size="xs" style={{ whiteSpace: 'pre-wrap' }}>{step.comments}</Text>
                          </Paper>
                        )}
                      </Stack>
                    )}

                    {/* Active step: instructions + doc checklist + actions */}
                    {isActive && (
                      <Stack gap="xs" mt="xs">
                        {def?.instructions && (
                          <Paper p="xs" radius="sm" withBorder style={{ borderColor: '#7C3AED33', backgroundColor: '#F5F3FF' }}>
                            <Group gap={6} mb={2}>
                              <IconInfoCircle size={12} style={{ color: '#7C3AED' }} />
                              <Text size="xs" fw={600} style={{ color: '#7C3AED' }}>Instructions</Text>
                            </Group>
                            <Text size="xs" style={{ whiteSpace: 'pre-wrap' }}>{def.instructions}</Text>
                          </Paper>
                        )}

                        {def?.documentChecklist && def.documentChecklist.length > 0 && (
                          <Paper p="xs" radius="sm" withBorder>
                            <Group gap={6} mb={4}>
                              <IconFileDescription size={12} style={{ color: '#0284C7' }} />
                              <Text size="xs" fw={600} c="blue">Required Documents</Text>
                            </Group>
                            {def.documentChecklist.map((doc: DocumentCheckItem) => (
                              <Group key={doc.key} gap={4} mb={2}>
                                <IconCircle size={8} style={{ color: doc.required ? '#DC2626' : '#64748B' }} />
                                <Text size="xs">{doc.label}</Text>
                                {doc.required && <Badge size="xs" color="red" variant="light">Required</Badge>}
                              </Group>
                            ))}
                          </Paper>
                        )}

                        {/* Action buttons */}
                        {userCanAct && (
                          <Group gap="xs">
                            {step.stepType === 'approval' && (
                              <>
                                <Button size="xs" color="green" leftSection={<IconCheck size={12} />} onClick={() => openAction('approve', step)}>Approve</Button>
                                <Button size="xs" color="red" variant="light" leftSection={<IconX size={12} />} onClick={() => openAction('reject', step)}>Reject</Button>
                              </>
                            )}
                            {step.stepType === 'review' && (
                              <Button size="xs" color="blue" leftSection={<IconCheck size={12} />} onClick={() => openAction('review', step)}>Mark Reviewed</Button>
                            )}
                            {step.stepType === 'task' && (
                              <Button size="xs" color="orange" leftSection={<IconCheck size={12} />} onClick={() => openAction('task', step)}>Mark Complete</Button>
                            )}
                            {step.stepType === 'signature' && (
                              <Button size="xs" color="teal" leftSection={<IconWriting size={12} />} onClick={() => openAction('signature', step)}>Sign</Button>
                            )}
                            {step.stepType === 'notification' && (
                              <Button size="xs" color="indigo" leftSection={<IconCheck size={12} />} onClick={() => openAction('task', step)}>Acknowledge</Button>
                            )}
                          </Group>
                        )}
                      </Stack>
                    )}

                    {/* Pending step */}
                    {!isDone && !isActive && (
                      <Text size="xs" c="dimmed" mt={2}>Waiting</Text>
                    )}
                  </Paper>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* ==== Action Modal ==== */}
      <Modal
        opened={actionType !== null}
        onClose={closeAction}
        title={
          actionType === 'approve' ? 'Approve Step' :
          actionType === 'reject' ? 'Reject Step' :
          actionType === 'review' ? 'Confirm Review' :
          actionType === 'task' ? 'Complete Task' :
          actionType === 'signature' ? 'Sign Step' : ''
        }
        size="md" centered closeOnClickOutside={!submitting}
      >
        {actionStep && (
          <Stack gap="md">
            <Paper p="sm" radius="sm" withBorder>
              <Text size="sm" fw={500}>{actionStep.stepName}</Text>
              <Text size="xs" c="dimmed">{instance.templateName} -- {instance.sourceRecordLabel}</Text>
            </Paper>

            {actionType === 'signature' && (
              <TextInput
                label="Type your full name to sign"
                placeholder="Full legal name..."
                value={signatureText}
                onChange={e => setSignatureText(e.target.value)}
                required
              />
            )}

            <Textarea
              label={actionType === 'reject' ? 'Reason for rejection (required)' : 'Comments (optional)'}
              placeholder={actionType === 'reject' ? 'Explain why this step is being rejected...' : 'Add any relevant notes...'}
              value={comments}
              onChange={e => setComments(e.target.value)}
              minRows={3} autosize
              required={actionType === 'reject'}
            />

            <Group justify="flex-end" gap="xs">
              <Button variant="subtle" onClick={closeAction} disabled={submitting}>Cancel</Button>
              <Button
                color={actionType === 'reject' ? 'red' : actionType === 'approve' ? 'green' : actionType === 'signature' ? 'teal' : 'blue'}
                leftSection={actionType === 'reject' ? <IconX size={14} /> : <IconCheck size={14} />}
                loading={submitting}
                disabled={(actionType === 'reject' && !comments.trim()) || (actionType === 'signature' && !signatureText.trim())}
                onClick={handleSubmitAction}
              >
                {actionType === 'approve' ? 'Approve' :
                 actionType === 'reject' ? 'Reject' :
                 actionType === 'review' ? 'Confirm Review' :
                 actionType === 'task' ? 'Mark Complete' :
                 actionType === 'signature' ? 'Submit Signature' : 'Submit'}
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* ==== Cancel Confirm Modal ==== */}
      <Modal opened={cancelConfirm} onClose={() => { if (!submitting) setCancelConfirm(false); }} title="Cancel Workflow" size="sm" centered>
        <Stack gap="md">
          <Text size="sm">Are you sure you want to cancel this workflow? This cannot be undone.</Text>
          <Paper p="sm" radius="sm" withBorder style={{ borderColor: '#FCA5A5', background: '#FEF2F2' }}>
            <Text size="sm" fw={500}>{instance.templateName}</Text>
            <Text size="xs" c="dimmed">{instance.sourceRecordLabel}</Text>
          </Paper>
          <Group justify="flex-end" gap="xs">
            <Button variant="subtle" onClick={() => setCancelConfirm(false)} disabled={submitting}>Keep Active</Button>
            <Button color="red" leftSection={<IconX size={14} />} loading={submitting} onClick={handleCancelWorkflow}>Cancel Workflow</Button>
          </Group>
        </Stack>
      </Modal>

      {/* ==== Reassign Modal ==== */}
      <Modal opened={reassignOpen} onClose={() => { if (!submitting) { setReassignOpen(false); setReassignEmail(''); } }} title="Reassign Current Step" size="sm" centered>
        <Stack gap="md">
          {(() => {
            const cs = instanceSteps.find(st => st.status === 'in_progress');
            return cs ? (
              <Paper p="sm" radius="sm" withBorder>
                <Text size="sm" fw={500}>{cs.stepName}</Text>
                <Text size="xs" c="dimmed">Currently assigned to: {cs.assigneeName || cs.assignee || '--'}</Text>
              </Paper>
            ) : (
              <Text size="sm" c="dimmed">No active step to reassign.</Text>
            );
          })()}
          <Select
            label="Reassign to"
            placeholder="Select user..."
            data={userOptions}
            value={reassignEmail}
            onChange={v => setReassignEmail(v ?? '')}
            searchable
            required
          />
          <Group justify="flex-end" gap="xs">
            <Button variant="subtle" onClick={() => { setReassignOpen(false); setReassignEmail(''); }} disabled={submitting}>Cancel</Button>
            <Button color="blue" leftSection={<IconUserEdit size={14} />} loading={submitting} disabled={!reassignEmail.trim()} onClick={handleReassign}>
              Reassign
            </Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
